import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { TrendingUp } from "lucide-react";

/**
 * PriceHint — iter88.12
 * Zeigt einen Preisvorschlag (Median vergleichbarer Angebote) an.
 *
 * Props:
 *   endpoint: "listing" | "addon" | "wear-days"
 *   params:   { area, category, subcategory, condition, size } | { area, code } | { area, days }
 *   onApply?: (median) => void   — Klick auf „Übernehmen"
 */
export default function PriceHint({ endpoint, params, onApply, testid = "price-hint" }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!endpoint) return;
    const hasRequired = endpoint === "addon"
      ? !!params?.code
      : endpoint === "wear-days"
        ? !!params?.days
        : !!params?.area;
    if (!hasRequired) return;
    let cancelled = false;
    setLoading(true);
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") query.set(k, v); });
    api.get(`/pricing/suggest/${endpoint}?${query.toString()}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [endpoint, JSON.stringify(params)]);

  if (loading || !data) return null;
  if (data.median == null) {
    return (
      <p data-testid={`${testid}-empty`} className="text-[10px] text-[#999] mt-1 inline-flex items-center gap-1">
        <TrendingUp size={10} /> Noch keine Vergleichsangebote
      </p>
    );
  }
  return (
    <div data-testid={testid} className="mt-1 flex items-center gap-2 text-[10px] text-[#666]">
      <TrendingUp size={10} className="text-[#0A0A0A]" />
      <span>{t("price_hint.average_price")} <b className="text-[#0A0A0A]">{data.median.toFixed(2)} EUR</b>{data.count ? ` (Median aus ${data.count})` : ""}</span>
      {data.min != null && data.max != null && (
        <span className="text-[#999]">· {data.min.toFixed(2)}–{data.max.toFixed(2)}</span>
      )}
      {onApply && (
        <button type="button" data-testid={`${testid}-apply`}
          onClick={() => onApply(data.median)}
          className="ml-1 text-[10px] text-[#0A0A0A] underline">{t("price_hint.apply")}</button>
      )}
    </div>
  );
}
